import React, { useState, useEffect, useRef, useCallback } from 'react'
import { fetchEmployees } from '../services/visitorApi'

export default function EmployeeDropdown({ value, onChange, error }) {
  const [query, setQuery] = useState('')
  const [employees, setEmployees] = useState([])
  const [selectedEmployee, setSelectedEmployee] = useState(null)
  const [isOpen, setIsOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [fetchError, setFetchError] = useState('')
  const wrapperRef = useRef(null)
  const debounceRef = useRef(null)
  const requestIdRef = useRef(0)

  const displayLabel = selectedEmployee
    ? `${selectedEmployee.name} (${selectedEmployee.department || 'N/A'})`
    : query

  useEffect(() => {
    if (!value) {
      setSelectedEmployee(null)
      setQuery('')
    }
  }, [value])

  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const loadEmployees = useCallback(async (searchTerm) => {
    const requestId = requestIdRef.current + 1
    requestIdRef.current = requestId
    setLoading(true)
    setFetchError('')
    try {
      const data = await fetchEmployees(searchTerm)
      if (requestId !== requestIdRef.current) return
      const list = Array.isArray(data) ? data : []
      setEmployees(list)
      if (value) {
        const found = list.find((e) => e.id === value)
        if (found) setSelectedEmployee(found)
      }
    } catch (err) {
      if (requestId !== requestIdRef.current) return
      console.error('Employee loading failed:', {
        message: err.message,
        status: err.status,
        data: err.data,
      })
      setFetchError(err.message || 'Could not load employees')
      setEmployees([])
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false)
      }
    }
  }, [value])

  const handleInputChange = (e) => {
    const text = e.target.value
    setQuery(text)
    if (!text) {
      onChange('')
      setSelectedEmployee(null)
    }
    setIsOpen(true)

    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      loadEmployees(text)
    }, 300)
  }

  const handleSelect = (employee) => {
    onChange(employee.id)
    setSelectedEmployee(employee)
    setQuery('')
    setIsOpen(false)
  }

  const handleFocus = () => {
    setIsOpen(true)
    loadEmployees(query || '')
  }

  return (
    <div className="relative" ref={wrapperRef}>
      <label htmlFor="employee" className="mb-1 block text-sm font-medium text-gray-700">
        Employee to Meet <span className="text-red-500">*</span>
      </label>
      <div className="relative">
        <input
          id="employee"
          type="text"
          role="combobox"
          aria-expanded={isOpen}
          aria-autocomplete="list"
          aria-controls="employee-listbox"
          autoComplete="off"
          placeholder="Search employee name..."
          value={displayLabel}
          onChange={handleInputChange}
          onFocus={handleFocus}
          className={`w-full rounded-lg border bg-white px-3 py-2.5 pr-10 text-sm text-gray-900 shadow-sm outline-none transition-colors placeholder:text-gray-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 ${
            error ? 'border-red-500 focus:border-red-500 focus:ring-red-200' : 'border-gray-300'
          }`}
        />
        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
          {loading ? (
            <svg className="h-4 w-4 animate-spin text-gray-400" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          ) : (
            <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          )}
        </div>
      </div>

      {isOpen && (
        <ul
          id="employee-listbox"
          role="listbox"
          className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
        >
          {fetchError && (
            <li className="px-3 py-2 text-sm text-red-600">{fetchError}</li>
          )}
          {!loading && employees.length === 0 && !fetchError && (
            <li className="px-3 py-2 text-sm text-gray-500">No employees found</li>
          )}
          {employees.map((employee) => (
            <li
              key={employee.id}
              role="option"
              aria-selected={employee.id === value}
              onClick={() => handleSelect(employee)}
              className={`cursor-pointer px-3 py-2.5 text-sm transition-colors hover:bg-gray-50 ${
                employee.id === value ? 'bg-primary-50 text-primary-700' : 'text-gray-900'
              }`}
            >
              <div className="font-medium">{employee.name}</div>
              <div className="text-xs text-gray-500">
                {[employee.department, employee.email].filter(Boolean).join(' - ') || 'No department'}
              </div>
            </li>
          ))}
        </ul>
      )}

      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  )
}
